import { EventEmitter, Injectable, Output } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { AppSettings } from 'src/app/core/configs/app.settings';
import { ServiceHelper } from 'src/app/core/helper/services.helper';

@Injectable({
  providedIn: 'root'
})
export class OneSignalService {

  @Output() pushHandler: EventEmitter<PushEvent> = new EventEmitter();

  private initialized = false;
  private oneSignalAppId = 'fbebae3b-4bdd-4228-a341-77c218028d05';
  private googleProjectNum = '880215369363';
  private oneSignal: any;
  private webOs: any;


  constructor() {
    if ((window as any).plugins != null) {
      this.oneSignal = (window as any).plugins.OneSignal;
      console.log('The Onesignal: ', this.oneSignal);
    }
  }

  // Allow web push
  async registerWithOneSignal(oneSignal: any = this.oneSignal) {
    try {
      // Run the initialization if we haven't already
      if (this.initialized === false) {
        await this.init(oneSignal);
      }
      // Set the device to be for the current user
      await this.updateTags({
        userId: `${ServiceHelper.authSvc.profile.userId}`,
        version: AppSettings.version
      });
    } catch (error) {
      console.error('Error registering with onesignal.', error);
    }
  }

  async getIsEnabled() {
    return new Promise<boolean>((resolve, reject) => {
      this.oneSignal.getDeviceState(device => {
        resolve(!device.pushDisabled);
      });
    });
  }

  setSubscription(subscribed: boolean) {
    this.oneSignal.disablePush(!subscribed);
  }


  async updateTags(newTags: any) {
    try {
      const currTags = await this.getCurrentTags();
      // Check if the current tags and new tags match
      if (JSON.stringify(newTags) === JSON.stringify(currTags)) {
        console.log('Current tags match existing tags.');
        return;
      }
      // They are new tags, so run the update
      const currTagKeys = Object.keys(currTags);
      // console.log('Current tags:', currTags);
      // console.log('New tags:', newTags);

      if (this.webOs != null) {
        await this.updateWebPushTags(currTagKeys, newTags);
      } else {
        await this.updateNativeTags(currTagKeys, newTags);
      }
    } catch (error) {
      console.error('Error updating tags on onesignal. Error:', error);
    }
  }

  sendTags(tags: any) {
    console.log('Sending tags to oneSignal: ', tags);
    if (this.webOs == null) {
      this.oneSignal.User.addTags(tags);
    } else {
      this.sendWebPushTags(tags);
    }
  }

  deleteTags(toDelete: string[]) {
    console.log('Clearing tags from oneSignal: ', toDelete);
    if (this.webOs == null) {
      this.oneSignal.User.removeTags(toDelete);
    } else {
      this.deleteWebPushTags(toDelete);
    }
  }

  async getUserId() {
    try {
      // Get the onesignal id's for the current device
      const ids = await this.oneSignal.getIds();
      return ids.userId;
    } catch (error) {
      throw error;
    }
  }

  deleteWebPushTags(toDelete: string[]) {
    console.log('Clearing tags from oneSignal: ', toDelete);
    // Add the delete tag function to the onesignal service worker
    this.webOs.push(() => {
      this.webOs.User.removeTags(toDelete).then(tagsDeleted => {
        // Tags were removed, so lets now send up the new ones
        console.log('Tags removed', tagsDeleted);
      }, error => {
        console.error('Error removing tags from onesignal. Error:', error);
      });
    });
  }

  sendWebPushTags(toSend: any) {
    console.log('registering tags with oneSignal: ', toSend);
    // Add the send tag function to the onesignal service worker
    this.webOs.push(() => {
      this.webOs.User.addTags(toSend).then(tagsSent => {
        // All done, so log it
        console.log('Tags sent', tagsSent);
      }, error => {
        // Error, may as well log it
        console.error('Error sending tags to onesignal. Error:', error);
      });
    });
  }

  updateWebPushTags(oldTags: string[], newTags: any) {
    try {
      this.deleteWebPushTags(oldTags);
      this.sendWebPushTags(newTags);
    } catch (error) {
      // Throw the error
      console.error('Web Onesignal update error: ', error);
      throw error;
    }
  }

  async updateNativeTags(oldTags: string[], newTags: any) {
    try {
      this.deleteTags(oldTags);
      this.sendTags(newTags);
    } catch (error) {
      // Throw the error
      console.error('Native Onesignal update error: ', error);
      throw error;
    }
  }

  async getCurrentTags() {
    if (Capacitor.isNativePlatform()) {
      return await this.oneSignal.User.getTags();
    } else {
      return new Promise((resolve, reject) => {
        this.webOs.push(() => {
          this.webOs.User.getTags()
            .then(tags => resolve(tags))
            .catch(error => reject(error));
        });
      });
    }
  }


  /**
   * Initialize the Onesignal Plugin
   * @param oneSignal Javascript reference if web. Defaults to native.
   */
  private async init(oneSignal: any = this.oneSignal) {
    console.log('Initializing onesignal: ' + this.oneSignalAppId);
    this.oneSignal = oneSignal;
    // Based on if the platform is capacitor or not we init a different way
    if (Capacitor.isNativePlatform()) {
      this.oneSignal.initialize(this.oneSignalAppId);
      this.oneSignal.Notifications.addEventListener('click', async (data: OnesignalPushMessage) => {
        const clickData = await data.notification;
        console.log('Onesignal Callback: ' + JSON.stringify(clickData));
        this.handlePush(data);
      });

      // iOS - Prompts the user for notification permissions.
      this.oneSignal.Notifications.requestPermission(true).then((success: Boolean) => {
        console.log("Notification permission granted " + success);
      })

      // oneSignal.startInit(this.oneSignalAppId, this.GOOGLE_PROJ_NUM);
      // oneSignal.inFocusDisplaying(this.oneSignal.OSInFocusDisplayOption.None); // no in-app alerts from onesignal
      // oneSignal.handleNotificationOpened().subscribe(data => {

      // });
      // oneSignal.endInit();
    } else if (oneSignal.push != null) {
      this.webOs = oneSignal;
      oneSignal.push(['init', {
        appId: this.oneSignalAppId,
        autoRegister: false,
        notifyButton: {
          enable: true /* Set to false to hide */
        }
      }]);
    }
    this.initialized = true;
  }

  /**
   * Ensures that some page is actually ready and listening for push event, then emits
   * the push data
   * @param data Onesignal data payload
   */
  private handlePush(data: OnesignalPushMessage) {
    setTimeout(() => {
      console.log('check for push observer, there are ' + this.pushHandler.observers.length);
      if (this.pushHandler.observers.length === 0) {
        console.log('no push handler...');
        this.handlePush(data);
      } else {
        console.log('The Push Data is as follows:', data);
        const t = data.notification.additionalData.type;
        const id = data.notification.additionalData.id;
        const accessCode = data.notification.additionalData.accessCode;
        console.log('emit push event with type ' + t + ', id ' + id);
        this.pushHandler.emit({ type: t, id: +id, accessCode: accessCode });
      }
    }, 500);
  }
}

export interface PushEvent {
  type: string;
  id: number;
  accessCode: string;
}

export interface AdditionalData {
  id: string;
  accessCode: string;
  type: string;
}

export interface RawPayload {
  "google.delivered_priority": string;
  "google.sent_time": number;
  "google.ttl": number;
  "google.original_priority": string;
  custom: string;
  pri: string;
  vis: string;
  from: string;
  alert: string;
  title: string;
  "google.message_id": string;
  "google.c.sender.id": string;
  androidNotificationId: number;
}

export interface Notification {
  notificationId: string;
  body: string;
  title: string;
  additionalData: AdditionalData;
  rawPayload: RawPayload;
  priority: number;
  fromProjectNumber: string;
  androidNotificationId: number;
}

export interface Action {
  type: number;
}

export interface OnesignalPushMessage {
  notification: Notification;
  action: Action;
}